import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiPlay } from 'react-icons/fi';
import { 
  enrichVocabularyWithStatus, 
  updateItemStatus,
  getItemStatus 
} from '../utils/vocabularyStatus';
import { extractAndProcessVocabulary } from '../utils/vocabularyUtils';
import { processVocabularyWithExamples } from '../utils/simpleVocabularyUtils';

// 格式化时间为 MM:SS 格式
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// 词性缩写转换
const abbreviatePartOfSpeech = (pos) => {
  if (!pos) return pos;
  const lowerPos = pos.toLowerCase().trim();
  const posMap = {
    'noun': 'n.',
    'verb': 'v.',
    'adjective': 'adj.',
    'adverb': 'adv.',
    'preposition': 'prep.',
    'conjunction': 'conj.',
    'pronoun': 'pron.',
    'interjection': 'interj.',
    'article': 'art.',
    'numeral': 'num.'
  };
  return posMap[lowerPos] || pos;
};

// 解析SRT字幕文件
const parseSRT = (srtContent) => {
  const subtitles = [];
  const blocks = srtContent.trim().split(/\n\s*\n/);
  
  blocks.forEach(block => {
    const lines = block.split('\n');
    if (lines.length >= 3) {
      const timeLine = lines[1];
      const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
      
      if (timeMatch) {
        const textLines = lines.slice(2);
        let english = '';
        let chinese = '';
        
        textLines.forEach(line => {
          if (/[\u4e00-\u9fa5]/.test(line)) {
            chinese += (chinese ? ' ' : '') + line;
          } else if (line.trim()) {
            english += (english ? ' ' : '') + line;
          }
        });
        
        if (english.trim()) {
          subtitles.push({
            english: english.trim(),
            chinese: chinese.trim()
          });
        }
      }
    }
  });
  
  return subtitles;
};

const EnglishCards = () => {
  // 英语卡片相关状态
  const [currentWordTab, setCurrentWordTab] = useState('words'); // words, phrases, expressions
  const [wordFilter, setWordFilter] = useState('all'); // all, unmarked, known, unknown
  const [phraseFilter, setPhraseFilter] = useState('all'); // all, unmarked, known, unknown
  const [expressionFilter, setExpressionFilter] = useState('all'); // all, unmarked, known, unknown
  const [showChinese, setShowChinese] = useState(true); // 是否显示中文
  const [showAnnotations, setShowAnnotations] = useState(true); // 是否显示标注
  const [uploadedVideos, setUploadedVideos] = useState([]); // 上传的视频数据
  const [selectedVideo, setSelectedVideo] = useState(null); // 当前选中的视频
  const [filteredWordsData, setFilteredWordsData] = useState([]); // 过滤后的单词数据
  const [filteredPhrasesData, setFilteredPhrasesData] = useState([]); // 过滤后的短语数据
  const [filteredExpressionsData, setFilteredExpressionsData] = useState([]); // 过滤后的地道表达数据
  const [isLoading, setIsLoading] = useState(true); // 数据加载状态
  const [isDataLoaded, setIsDataLoaded] = useState(false); // 数据是否加载完成
  const [isVirtualVocabularyCleared, setIsVirtualVocabularyCleared] = useState(false); // 虚拟词汇是否已清除
  const [windowWidth, setWindowWidth] = useState(window.innerWidth); // 窗口宽度
  const MIN_WIDTH = 1200; // 最小允许宽度

  // 获取所有视频数据
  const getAllVideos = async () => {
    try {
      // 从服务器API获取所有视频数据
      const response = await fetch('/api/videos');
      const data = await response.json();
      
      if (data.success && data.videos) {
        // 转换数据格式以匹配前端期望的格式
        const uploadedVideos = data.videos.map(video => ({
          id: video.videoId,
          title: video.title,
          description: '',
          category: video.category,
          subcategory: video.subcategory || '',
          level: 'Starters', // 使用默认值，或者从视频数据中获取
          duration: video.duration || 0,
          fileUrl: video.videoUrl.startsWith('http') ? video.videoUrl : `${window.location.origin}${video.videoUrl}`,
          subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
            ? (video.subtitleFiles[0].startsWith('http') 
                ? video.subtitleFiles[0] 
                : `${window.location.origin}/uploads/${video.subtitleFiles[0]}`)
            : '',
          posterUrl: video.posterUrl 
            ? (video.posterUrl.startsWith('http') 
                ? video.posterUrl 
                : `${window.location.origin}${video.posterUrl}`)
            : '',
          fileType: video.fileType || 'video',
          hidden: false,
          uploadedAt: video.uploadTime,
          subtitleFiles: video.subtitleFiles || []
        }));
        setUploadedVideos(uploadedVideos);
      } else {
        // 如果服务器API失败，尝试从本地存储获取
        const uploadedVideos = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
        setUploadedVideos(uploadedVideos);
      }
      
      // 确保词汇数据为空
      setFilteredWordsData([]);
      setFilteredPhrasesData([]);
      setFilteredExpressionsData([]);
      
      // 数据加载完成
      setIsLoading(false);
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Failed to get all videos:', error);
      // 确保词汇数据为空
      setFilteredWordsData([]);
      setFilteredPhrasesData([]);
      setFilteredExpressionsData([]);
      setIsLoading(false);
      setIsDataLoaded(true);
    }
  };

  // 根据选中的视频过滤词汇数据
  const filterVocabularyData = async (video = selectedVideo) => {
    if (video) {
      // 清理videoId，移除可能存在的uploaded_前缀，与Lesson.js保持一致
      let cleanVideoId = video.id;
      if (cleanVideoId.includes('uploaded_')) {
        cleanVideoId = cleanVideoId.replace('uploaded_', '');
      }
      console.log('英语卡片页面清理后的videoId:', cleanVideoId, '原始videoId:', video.id);
      
      let subtitles = [];
      
      // 先加载字幕文件
      try {
        const subtitleResponse = await fetch(video.subtitleUrl);
        if (subtitleResponse.ok) {
          const subtitleText = await subtitleResponse.text();
          subtitles = parseSRT(subtitleText);
          console.log('英语卡片页面加载字幕成功:', subtitles.length);
        }
      } catch (error) {
        console.log('英语卡片页面加载字幕失败:', error);
      }
      
      try {
        // 优先使用 extractAndProcessVocabulary，优先从后端获取已保存的词汇数据
        const vocabulary = await extractAndProcessVocabulary(video.subtitleUrl, cleanVideoId, false);
        
        // 使用全新的简单工具处理词汇和例句
        let processedVocabulary = vocabulary;
        if (subtitles.length > 0) {
          processedVocabulary = processVocabularyWithExamples(vocabulary, subtitles);
        }
        
        // 更新 localStorage 缓存
        const vocabularyKey = `adminVocabulary_${cleanVideoId}`;
        localStorage.setItem(vocabularyKey, JSON.stringify(processedVocabulary));
        console.log('英语卡片页面词汇数据已更新到缓存:', processedVocabulary);
        
        // 使用统一的状态管理工具加载词汇状态
        const vocabularyWithStatus = enrichVocabularyWithStatus(cleanVideoId, processedVocabulary);
        
        setFilteredWordsData(vocabularyWithStatus.words);
        setFilteredPhrasesData(vocabularyWithStatus.phrases);
        setFilteredExpressionsData(vocabularyWithStatus.expressions);
      } catch (error) {
        console.error('Failed to get vocabulary data:', error);
        // 如果发生错误，尝试从本地存储获取
        const vocabularyKey = `adminVocabulary_${cleanVideoId}`;
        const savedVocabulary = localStorage.getItem(vocabularyKey);
        const vocabulary = savedVocabulary ? JSON.parse(savedVocabulary) : {};
        
        // 使用统一的状态管理工具加载词汇状态
        const vocabularyWithStatus = enrichVocabularyWithStatus(cleanVideoId, vocabulary);
        
        setFilteredWordsData(vocabularyWithStatus.words);
        setFilteredPhrasesData(vocabularyWithStatus.phrases);
        setFilteredExpressionsData(vocabularyWithStatus.expressions);
      }
    } else {
      // 如果没有选中视频，不显示任何数据
      setFilteredWordsData([]);
      setFilteredPhrasesData([]);
      setFilteredExpressionsData([]);
    }
  };

  // 检查localStorage中的所有数据
  const checkLocalStorage = () => {
    try {
      console.log('=== 检查localStorage中的数据 ===');
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        keys.push(localStorage.key(i));
      }
      
      console.log('LocalStorage中的所有键:', keys);
      
      // 检查与词汇相关的键
      keys.forEach(key => {
        if (key.includes('adminVocabulary_') || key.includes('vocabulary')) {
          const value = localStorage.getItem(key);
          console.log(`=== ${key} ===`);
          try {
            const parsedValue = JSON.parse(value);
            if (parsedValue.words) {
              console.log('Words:', parsedValue.words.map(w => w.word));
            }
            if (parsedValue.phrases) {
              console.log('Phrases:', parsedValue.phrases.map(p => p.phrase));
            }
            if (parsedValue.expressions) {
              console.log('Expressions:', parsedValue.expressions.map(e => e.expression));
            }
          } catch (error) {
            console.log('Value:', value);
          }
        }
      });
      
      console.log('=== 检查完成 ===');
    } catch (error) {
      console.error('检查localStorage失败:', error);
    }
  };

  // 清除所有可能的虚拟词汇数据 - 彻底清除
  const clearAllVirtualVocabulary = () => {
    try {
      console.log('=== 开始彻底清除所有虚拟词汇数据 ===');
      
      // 定义虚拟词汇列表（更全面的列表）
      const virtualWords = ['pensive', 'introspective', 'journal', 'break down', 'analytical'];
      const virtualPhrases = ['in my head', 'thought processes'];
      const virtualExpressions = ['overall'];
      
      // 获取所有localStorage键
      const allKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        allKeys.push(localStorage.key(i));
      }
      
      let removedCount = 0;
      
      // 1. 检查并清理包含虚拟词汇的词汇数据
      allKeys.forEach(key => {
        if (key.includes('adminVocabulary_')) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const data = JSON.parse(value);
              let hasVirtual = false;
              
              // 过滤单词数据
              if (data.words) {
                const originalLength = data.words.length;
                data.words = data.words.filter(word => !virtualWords.includes(word.word));
                hasVirtual = hasVirtual || data.words.length !== originalLength;
              }
              
              // 过滤短语数据
              if (data.phrases) {
                const originalLength = data.phrases.length;
                data.phrases = data.phrases.filter(phrase => !virtualPhrases.includes(phrase.phrase));
                hasVirtual = hasVirtual || data.phrases.length !== originalLength;
              }
              
              // 过滤地道表达数据
              if (data.expressions) {
                const originalLength = data.expressions.length;
                data.expressions = data.expressions.filter(expr => !virtualExpressions.includes(expr.expression));
                hasVirtual = hasVirtual || data.expressions.length !== originalLength;
              }
              
              // 如果有虚拟词汇被过滤掉，更新数据
              if (hasVirtual) {
                console.log(`清理包含虚拟词汇的数据: ${key}`);
                localStorage.setItem(key, JSON.stringify(data));
                removedCount++;
              }
            } catch (e) {
              console.log(`解析数据失败: ${key}`, e);
            }
          }
        }
      });
      
      // 2. 清除可能包含虚拟词汇的其他数据
      const suspectKeys = ['wordsData', 'phrasesData', 'expressionsData', 'allWordsData', 'allPhrasesData', 'allExpressionsData'];
      suspectKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log(`清除可疑数据: ${key}`);
          localStorage.removeItem(key);
          removedCount++;
        }
      });
      
      console.log(`彻底清除完成，共处理 ${removedCount} 个数据项`);
      
      // 设置虚拟词汇已清除
      setIsVirtualVocabularyCleared(true);
    } catch (error) {
      console.error('彻底清除虚拟词汇数据失败:', error);
      // 即使出错也设置为已清除，避免阻塞
      setIsVirtualVocabularyCleared(true);
    }
  };

  // 清除旧的虚拟词汇数据
  const clearOldVocabularyData = () => {
    try {
      // 检查并清除旧格式的词汇数据
      const uploadedVideos = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
      uploadedVideos.forEach(video => {
        const videoId = video.id;
        // 清除旧格式的词汇数据
        localStorage.removeItem(`adminVocabulary_${videoId}_words`);
        localStorage.removeItem(`adminVocabulary_${videoId}_phrases`);
        localStorage.removeItem(`adminVocabulary_${videoId}_expressions`);
      });
      
      // 清除可能的虚拟词汇数据
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        keys.push(localStorage.key(i));
      }
      
      keys.forEach(key => {
        if (key.includes('adminVocabulary_')) {
          const savedVocabulary = localStorage.getItem(key);
          if (savedVocabulary) {
            try {
              const vocabulary = JSON.parse(savedVocabulary);
              
              // 检查是否包含虚拟词汇
              const hasVirtualWords = vocabulary.words && vocabulary.words.some(word => 
                word.word === 'pensive' || word.word === 'introspective'
              );
              
              const hasVirtualPhrases = vocabulary.phrases && vocabulary.phrases.some(phrase => 
                phrase.phrase === 'in my head' || phrase.phrase === 'thought processes'
              );
              
              const hasVirtualExpressions = vocabulary.expressions && vocabulary.expressions.some(expr => 
                expr.expression === 'overall'
              );
              
              if (hasVirtualWords || hasVirtualPhrases || hasVirtualExpressions) {
                console.log(`Removing virtual vocabulary data: ${key}`);
                localStorage.removeItem(key);
              }
            } catch (error) {
              console.error('Failed to parse vocabulary data:', error);
            }
          }
        }
      });
    } catch (error) {
      console.error('Failed to clear old vocabulary data:', error);
    }
  };

  useEffect(() => {
    // 首先确保词汇数据为空
    setFilteredWordsData([]);
    setFilteredPhrasesData([]);
    setFilteredExpressionsData([]);
    
    // 检查localStorage中的数据
    checkLocalStorage();
    // 清除所有可能的虚拟词汇数据
    clearAllVirtualVocabulary();
    // 清除旧的虚拟词汇数据
    clearOldVocabularyData();
  }, []);

  // 当虚拟词汇清除完成后，加载视频数据
  useEffect(() => {
    if (isVirtualVocabularyCleared) {
      // 只加载视频列表，不加载词汇数据
      getAllVideos();
    }
  }, [isVirtualVocabularyCleared]);

  // 当选中视频变化时，加载对应视频的词汇数据
  useEffect(() => {
    // 立即清空词汇数据，避免显示旧数据
    setFilteredWordsData([]);
    setFilteredPhrasesData([]);
    setFilteredExpressionsData([]);
    
    if (selectedVideo) {
      // 直接调用，避免延迟
      filterVocabularyData(selectedVideo);
    }
  }, [selectedVideo]);

  // 监听词汇状态更新事件，实现跨页面同步
  useEffect(() => {
    const handleStatusUpdate = (event) => {
      const { videoId, itemId, itemType, status } = event.detail;
      
      // 只处理当前选中视频的状态更新
      if (selectedVideo) {
        // 清理videoId，确保匹配
        let cleanSelectedVideoId = selectedVideo.id;
        if (cleanSelectedVideoId.includes('uploaded_')) {
          cleanSelectedVideoId = cleanSelectedVideoId.replace('uploaded_', '');
        }
        
        if (cleanSelectedVideoId === videoId || selectedVideo.id === videoId) {
          // 根据类型更新对应的数据
          if (itemType === 'word') {
            setFilteredWordsData(prevWords => 
              prevWords.map(word => 
                word.id === itemId ? { ...word, status } : word
              )
            );
          } else if (itemType === 'phrase') {
            setFilteredPhrasesData(prevPhrases => 
              prevPhrases.map(phrase => 
                phrase.id === itemId ? { ...phrase, status } : phrase
              )
            );
          } else if (itemType === 'expression') {
            setFilteredExpressionsData(prevExpressions => 
              prevExpressions.map(expression => 
                expression.id === itemId ? { ...expression, status } : expression
              )
            );
          }
        }
      }
    };

    window.addEventListener('vocabularyStatusUpdated', handleStatusUpdate);
    
    return () => {
      window.removeEventListener('vocabularyStatusUpdated', handleStatusUpdate);
    };
  }, [selectedVideo]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 更新单词状态
  const updateWordStatus = (id, status) => {
    if (selectedVideo) {
      // 清理videoId，确保与Lesson.js一致
      let cleanVideoId = selectedVideo.id;
      if (cleanVideoId.includes('uploaded_')) {
        cleanVideoId = cleanVideoId.replace('uploaded_', '');
      }
      
      // 使用统一的状态管理工具保存状态
      updateItemStatus(cleanVideoId, id, 'word', status);
      
      // 更新本地状态
      const newStatus = getItemStatus(cleanVideoId, id, 'word');
      setFilteredWordsData(prevWords => 
        prevWords.map(word => {
          if (word.id === id) {
            return { ...word, status: newStatus };
          }
          return word;
        })
      );
      
      // 触发事件，通知其他页面更新
      window.dispatchEvent(new CustomEvent('vocabularyStatusUpdated', {
        detail: { videoId: cleanVideoId, itemId: id, itemType: 'word', status: newStatus }
      }));
    }
  };

  // 更新短语状态
  const updatePhraseStatus = (id, status) => {
    if (selectedVideo) {
      // 清理videoId，确保与Lesson.js一致
      let cleanVideoId = selectedVideo.id;
      if (cleanVideoId.includes('uploaded_')) {
        cleanVideoId = cleanVideoId.replace('uploaded_', '');
      }
      
      // 使用统一的状态管理工具保存状态
      updateItemStatus(cleanVideoId, id, 'phrase', status);
      
      // 更新本地状态
      const newStatus = getItemStatus(cleanVideoId, id, 'phrase');
      setFilteredPhrasesData(prevPhrases => 
        prevPhrases.map(phrase => {
          if (phrase.id === id) {
            return { ...phrase, status: newStatus };
          }
          return phrase;
        })
      );
      
      // 触发事件，通知其他页面更新
      window.dispatchEvent(new CustomEvent('vocabularyStatusUpdated', {
        detail: { videoId: cleanVideoId, itemId: id, itemType: 'phrase', status: newStatus }
      }));
    }
  };

  // 更新地道表达状态
  const updateExpressionStatus = (id, status) => {
    if (selectedVideo) {
      // 清理videoId，确保与Lesson.js一致
      let cleanVideoId = selectedVideo.id;
      if (cleanVideoId.includes('uploaded_')) {
        cleanVideoId = cleanVideoId.replace('uploaded_', '');
      }
      
      // 使用统一的状态管理工具保存状态
      updateItemStatus(cleanVideoId, id, 'expression', status);
      
      // 更新本地状态
      const newStatus = getItemStatus(cleanVideoId, id, 'expression');
      setFilteredExpressionsData(prevExpressions => 
        prevExpressions.map(expression => {
          if (expression.id === id) {
            return { ...expression, status: newStatus };
          }
          return expression;
        })
      );
      
      // 触发事件，通知其他页面更新
      window.dispatchEvent(new CustomEvent('vocabularyStatusUpdated', {
        detail: { videoId: cleanVideoId, itemId: id, itemType: 'expression', status: newStatus }
      }));
    }
  };

  // 保存不认识的单词到本地存储
  const saveUnknownWord = (word) => {
    try {
      const savedWords = JSON.parse(localStorage.getItem('unknownWords') || '[]');
      const wordExists = savedWords.some(w => w.word === word.word);
      if (!wordExists) {
        const newWord = {
          id: Date.now(),
          word: word.word,
          phonetic: word.phonetic || '',
          definition: word.definition || '',
          synonyms: word.synonyms || '',
          sentence: word.sentence || '',
          sentenceChinese: word.sentenceChinese || '',
          addedAt: new Date().toISOString()
        };
        savedWords.push(newWord);
        localStorage.setItem('unknownWords', JSON.stringify(savedWords));
        console.log('单词已保存到单词卡:', word.word);
      }
      updateWordStatus(word.id, 'unknown');
    } catch (error) {
      console.error('保存单词失败:', error);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', color: '#1a2b4c', position: 'relative' }}>
      {/* 白色遮罩层 - 当屏幕宽度小于最小宽度时显示 */}
      {windowWidth < MIN_WIDTH && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📐</div>
          <h2 style={{ color: '#1a2b4c', marginBottom: '1rem', fontSize: '1.5rem' }}>请将屏幕宽度调宽</h2>
          <p style={{ color: '#666', fontSize: '1rem', maxWidth: '400px' }}>
            英语卡片功能需要更宽的屏幕才能正常使用，请将浏览器窗口调宽至 {MIN_WIDTH}px 以上。
          </p>
        </div>
      )}

      {/* 主要内容区 */}
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* 左侧视频导航 */}
        <div style={{
          width: '380px',
          backgroundColor: '#f8f9fa',
          borderRight: '1px solid rgba(0, 0, 0, 0.1)',
          padding: '1.5rem',
          overflowY: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <Link to="/" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#0066ff',
              textDecoration: 'none',
              padding: '0.6rem 0.8rem',
              borderRadius: '8px',
              backgroundColor: 'white',
              border: '1px solid rgba(0, 102, 255, 0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(0, 102, 255, 0.05)';
              e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.boxShadow = 'none';
            }}>
              <FiArrowLeft />
            </Link>
            <h3 style={{
              color: '#1a2b4c',
              fontSize: '1.2rem',
              fontWeight: '600',
              margin: 0
            }}>导航</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {uploadedVideos.map((video) => (
              <div 
                key={video.id} 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '1rem',
                  borderRadius: '24px',
                  backgroundColor: selectedVideo && selectedVideo.id === video.id ? 'rgba(0, 102, 255, 0.1)' : '#ffffff',
                  border: selectedVideo && selectedVideo.id === video.id ? '2px solid rgba(0, 102, 255, 0.5)' : '2px solid rgba(0, 102, 255, 0.2)',
                  boxShadow: '0 8px 24px rgba(0, 102, 255, 0.12)',
                  textDecoration: 'none',
                  color: '#1a2b4c',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  minHeight: '150px'
                }}
                onClick={() => {
                  // 立即清空词汇数据
                  setFilteredWordsData([]);
                  setFilteredPhrasesData([]);
                  setFilteredExpressionsData([]);
                  
                  // 设置选中的视频
                  setSelectedVideo(video);
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateX(5px)';
                  e.target.style.boxShadow = '0 2px 10px rgba(0, 102, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateX(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.8rem', flex: 1 }}>
                  {/* 截图封面 */}
                  <div style={{
                    width: '80px',
                    height: '60px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(0, 102, 255, 0.1)'
                  }}>
                    {video.posterUrl && (
                      <img 
                        src={video.posterUrl} 
                        alt={video.title} 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }} 
                      />
                    )}
                  </div>
                  {/* 标题 */}
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', wordWrap: 'break-word' }}>{video.title}</h4>
                  </div>
                </div>
                {/* 分类、难易度、时长、文件类型 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'nowrap', flex: 1 }}>
                    <span style={{
                      backgroundColor: 'rgba(0, 102, 255, 0.1)',
                      color: '#0066ff',
                      padding: '2px 8px',
                      borderRadius: '16px',
                      fontSize: '10px',
                      fontWeight: '600',
                      border: '1px solid rgba(0, 102, 255, 0.2)',
                      minWidth: '90px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {(() => {
                        const categoryMap = {
                          'universe': '宇宙与空间科学',
                          'physics': '物理学',
                          'chemistry': '化学',
                          'earth': '地球科学',
                          'biology': '基础生物学',
                          'plants': '植物学',
                          'animals': '动物学',
                          'microbes': '微生物学',
                          'ecology': '生态学',
                          'human': '人体科学'
                        };
                        return categoryMap[video.category] || (video.category ? video.category : '未分类');
                      })()}
                    </span>
                    <span style={{
                      backgroundColor: 'rgba(134, 239, 172, 0.3)',
                      color: '#15803d',
                      padding: '2px 8px',
                      borderRadius: '16px',
                      fontSize: '10px',
                      fontWeight: '600',
                      border: '1px solid rgba(134, 239, 172, 0.4)',
                      minWidth: '60px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {(() => {
                        const levelMap = {
                          'Starters': 'Starters',
                          'Movers': 'Movers',
                          'Flyers': 'Flyers',
                          'KET': 'KET',
                          'PET': 'PET',
                          'FCE': 'FCE'
                        };
                        return levelMap[video.level] || (video.level ? video.level : '未设置');
                      })()}
                    </span>
                    <span style={{
                      backgroundColor: 'rgba(251, 191, 36, 0.2)',
                      color: '#b45309',
                      padding: '2px 8px',
                      borderRadius: '16px',
                      fontSize: '10px',
                      fontWeight: '600',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                      minWidth: '65px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {video.duration ? formatTime(video.duration) : '未知时长'}
                    </span>
                    <span style={{
                      backgroundColor: 'rgba(192, 132, 252, 0.2)',
                      color: '#7c3aed',
                      padding: '2px 8px',
                      borderRadius: '16px',
                      fontSize: '10px',
                      fontWeight: '600',
                      border: '1px solid rgba(192, 132, 252, 0.3)',
                      minWidth: '50px',
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {video.fileType === 'audio' ? '🎵 音频' : '🎬 视频'}
                    </span>
                  </div>
                </div>
                {/* 上传日期 */}
                <div style={{ textAlign: 'right', fontSize: '10px', color: 'rgba(26, 43, 76, 0.7)' }}>
                  {new Date(video.uploadDate || Date.now()).toLocaleDateString()}
                </div>
              </div>
            ))}
            {uploadedVideos.length === 0 && (
              <p style={{
                color: 'rgba(26, 43, 76, 0.6)',
                fontSize: '0.9rem',
                textAlign: 'center',
                padding: '2rem 0'
              }}>
                还没有上传视频<br/>去上传视频吧！
              </p>
            )}
          </div>
        </div>

        {/* 右侧词汇区域 */}
        <div style={{ flex: 1, padding: '2rem' }}>
          {/* 标签导航 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
            borderBottom: '1px solid #e0e0e0',
            paddingBottom: '1rem'
          }}>
            <button
              onClick={() => setCurrentWordTab('words')}
              style={{
                flex: 1,
                marginRight: '0.5rem',
                padding: '1rem 1.5rem',
                borderRadius: '8px',
                border: currentWordTab === 'words' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                backgroundColor: currentWordTab === 'words' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                color: currentWordTab === 'words' ? '#1a73e8' : '#333',
                cursor: 'pointer',
                fontWeight: currentWordTab === 'words' ? '600' : '400',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (currentWordTab !== 'words') {
                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(26, 115, 232, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentWordTab !== 'words') {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              单词 ({filteredWordsData.length})
            </button>
            <button
              onClick={() => setCurrentWordTab('phrases')}
              style={{
                flex: 1,
                marginLeft: '0.25rem',
                marginRight: '0.25rem',
                padding: '1rem 1.5rem',
                borderRadius: '8px',
                border: currentWordTab === 'phrases' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                backgroundColor: currentWordTab === 'phrases' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                color: currentWordTab === 'phrases' ? '#1a73e8' : '#333',
                cursor: 'pointer',
                fontWeight: currentWordTab === 'phrases' ? '600' : '400',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (currentWordTab !== 'phrases') {
                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(26, 115, 232, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentWordTab !== 'phrases') {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              短语 ({filteredPhrasesData.length})
            </button>
            <button
              onClick={() => setCurrentWordTab('expressions')}
              style={{
                flex: 1,
                marginLeft: '0.5rem',
                padding: '1rem 1.5rem',
                borderRadius: '8px',
                border: currentWordTab === 'expressions' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                backgroundColor: currentWordTab === 'expressions' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                color: currentWordTab === 'expressions' ? '#1a73e8' : '#333',
                cursor: 'pointer',
                fontWeight: currentWordTab === 'expressions' ? '600' : '400',
                fontSize: '1.1rem',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (currentWordTab !== 'expressions') {
                  e.target.style.backgroundColor = 'rgba(26, 115, 232, 0.05)';
                  e.target.style.borderColor = 'rgba(26, 115, 232, 0.3)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(26, 115, 232, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentWordTab !== 'expressions') {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e0e0e0';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              自然科普词汇 ({filteredExpressionsData.length})
            </button>

          </div>





          {/* 单词列表 */}
          <div style={{
            minHeight: '400px'
          }}>
            {/* 加载中状态 */}
            {isLoading || !isVirtualVocabularyCleared ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '400px',
                color: 'rgba(26, 43, 76, 0.6)'
              }}>
                加载中...
              </div>
            ) : (
              <>
                {/* 显示单词 - 最终验证过滤 */}
                {(() => {
                  // 定义虚拟词汇列表用于最终验证
                  const virtualWords = ['pensive', 'introspective', 'journal', 'break down', 'analytical'];
                  const virtualPhrases = ['in my head', 'thought processes'];
                  const virtualExpressions = ['overall'];
                  
                  // 最终过滤掉任何虚拟词汇
                  const virtualWordsNoVirtual = filteredWordsData.filter(word => !virtualWords.includes(word.word));
                  const finalFilteredPhrases = filteredPhrasesData.filter(phrase => !virtualPhrases.includes(phrase.phrase));
                  const finalFilteredExpressions = filteredExpressionsData.filter(expr => {
                    const text = expr.expression || expr.word || expr.phrase || '';
                    return !virtualExpressions.includes(text);
                  });

                  // 计算单词各状态的数量
                  const wordCounts = {
                    all: virtualWordsNoVirtual.length,
                    unmarked: virtualWordsNoVirtual.filter(w => w.status === 'unmarked').length,
                    known: virtualWordsNoVirtual.filter(w => w.status === 'known').length,
                    unknown: virtualWordsNoVirtual.filter(w => w.status === 'unknown').length
                  };

                  // 计算短语各状态的数量
                  const phraseCounts = {
                    all: finalFilteredPhrases.length,
                    unmarked: finalFilteredPhrases.filter(p => p.status === 'unmarked').length,
                    known: finalFilteredPhrases.filter(p => p.status === 'known').length,
                    unknown: finalFilteredPhrases.filter(p => p.status === 'unknown').length
                  };

                  // 计算地道表达各状态的数量
                  const expressionCounts = {
                    all: finalFilteredExpressions.length,
                    unmarked: finalFilteredExpressions.filter(e => e.status === 'unmarked').length,
                    known: finalFilteredExpressions.filter(e => e.status === 'known').length,
                    unknown: finalFilteredExpressions.filter(e => e.status === 'unknown').length
                  };

                  // 根据 wordFilter 过滤单词
                  const filteredWordsByStatus = virtualWordsNoVirtual.filter(word => {
                    if (wordFilter === 'all') return true;
                    return word.status === wordFilter;
                  });

                  // 根据 phraseFilter 过滤短语
                  const filteredPhrasesByStatus = finalFilteredPhrases.filter(phrase => {
                    if (phraseFilter === 'all') return true;
                    return phrase.status === phraseFilter;
                  });

                  // 根据 expressionFilter 过滤地道表达
                  const filteredExpressionsByStatus = finalFilteredExpressions.filter(expression => {
                    if (expressionFilter === 'all') return true;
                    return expression.status === expressionFilter;
                  });
                  
                  return (
                    <>
                      {/* 单词过滤器按钮 */}
                      {currentWordTab === 'words' && (
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          marginBottom: '1.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => setWordFilter('all')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: wordFilter === 'all' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: wordFilter === 'all' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: wordFilter === 'all' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: wordFilter === 'all' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            全部 ({wordCounts.all})
                          </button>
                          <button
                            onClick={() => setWordFilter('unmarked')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: wordFilter === 'unmarked' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: wordFilter === 'unmarked' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: wordFilter === 'unmarked' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: wordFilter === 'unmarked' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            未标记 ({wordCounts.unmarked})
                          </button>
                          <button
                            onClick={() => setWordFilter('known')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: wordFilter === 'known' ? '2px solid #28a745' : '1px solid #e0e0e0',
                              backgroundColor: wordFilter === 'known' ? 'rgba(40, 167, 69, 0.1)' : 'white',
                              color: wordFilter === 'known' ? '#28a745' : '#333',
                              cursor: 'pointer',
                              fontWeight: wordFilter === 'known' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            认识 ({wordCounts.known})
                          </button>
                          <button
                            onClick={() => setWordFilter('unknown')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: wordFilter === 'unknown' ? '2px solid #dc3545' : '1px solid #e0e0e0',
                              backgroundColor: wordFilter === 'unknown' ? 'rgba(220, 53, 69, 0.1)' : 'white',
                              color: wordFilter === 'unknown' ? '#dc3545' : '#333',
                              cursor: 'pointer',
                              fontWeight: wordFilter === 'unknown' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            不认识 ({wordCounts.unknown})
                          </button>
                          <button
                            onClick={() => setShowChinese(!showChinese)}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: '1px solid #e0e0e0',
                              backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: showChinese ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: showChinese ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {showChinese ? '隐藏中文' : '显示中文'}
                          </button>
                        </div>
                      )}
                      {/* 短语过滤器按钮 */}
                      {currentWordTab === 'phrases' && (
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          marginBottom: '1.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => setPhraseFilter('all')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: phraseFilter === 'all' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: phraseFilter === 'all' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: phraseFilter === 'all' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: phraseFilter === 'all' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            全部 ({phraseCounts.all})
                          </button>
                          <button
                            onClick={() => setPhraseFilter('unmarked')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: phraseFilter === 'unmarked' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: phraseFilter === 'unmarked' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: phraseFilter === 'unmarked' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: phraseFilter === 'unmarked' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            未标记 ({phraseCounts.unmarked})
                          </button>
                          <button
                            onClick={() => setPhraseFilter('known')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: phraseFilter === 'known' ? '2px solid #28a745' : '1px solid #e0e0e0',
                              backgroundColor: phraseFilter === 'known' ? 'rgba(40, 167, 69, 0.1)' : 'white',
                              color: phraseFilter === 'known' ? '#28a745' : '#333',
                              cursor: 'pointer',
                              fontWeight: phraseFilter === 'known' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            认识 ({phraseCounts.known})
                          </button>
                          <button
                            onClick={() => setPhraseFilter('unknown')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: phraseFilter === 'unknown' ? '2px solid #dc3545' : '1px solid #e0e0e0',
                              backgroundColor: phraseFilter === 'unknown' ? 'rgba(220, 53, 69, 0.1)' : 'white',
                              color: phraseFilter === 'unknown' ? '#dc3545' : '#333',
                              cursor: 'pointer',
                              fontWeight: phraseFilter === 'unknown' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            不认识 ({phraseCounts.unknown})
                          </button>
                          <button
                            onClick={() => setShowChinese(!showChinese)}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: '1px solid #e0e0e0',
                              backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: showChinese ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: showChinese ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {showChinese ? '隐藏中文' : '显示中文'}
                          </button>
                        </div>
                      )}
                      {/* 地道表达过滤器按钮 */}
                      {currentWordTab === 'expressions' && (
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          marginBottom: '1.5rem',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => setExpressionFilter('all')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: expressionFilter === 'all' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: expressionFilter === 'all' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: expressionFilter === 'all' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: expressionFilter === 'all' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            全部 ({expressionCounts.all})
                          </button>
                          <button
                            onClick={() => setExpressionFilter('unmarked')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: expressionFilter === 'unmarked' ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                              backgroundColor: expressionFilter === 'unmarked' ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: expressionFilter === 'unmarked' ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: expressionFilter === 'unmarked' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            未标记 ({expressionCounts.unmarked})
                          </button>
                          <button
                            onClick={() => setExpressionFilter('known')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: expressionFilter === 'known' ? '2px solid #28a745' : '1px solid #e0e0e0',
                              backgroundColor: expressionFilter === 'known' ? 'rgba(40, 167, 69, 0.1)' : 'white',
                              color: expressionFilter === 'known' ? '#28a745' : '#333',
                              cursor: 'pointer',
                              fontWeight: expressionFilter === 'known' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            认识 ({expressionCounts.known})
                          </button>
                          <button
                            onClick={() => setExpressionFilter('unknown')}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: expressionFilter === 'unknown' ? '2px solid #dc3545' : '1px solid #e0e0e0',
                              backgroundColor: expressionFilter === 'unknown' ? 'rgba(220, 53, 69, 0.1)' : 'white',
                              color: expressionFilter === 'unknown' ? '#dc3545' : '#333',
                              cursor: 'pointer',
                              fontWeight: expressionFilter === 'unknown' ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            不认识 ({expressionCounts.unknown})
                          </button>
                          <button
                            onClick={() => setShowChinese(!showChinese)}
                            style={{
                              padding: '0.6rem 1rem',
                              borderRadius: '6px',
                              border: '1px solid #e0e0e0',
                              backgroundColor: showChinese ? 'rgba(26, 115, 232, 0.1)' : 'white',
                              color: showChinese ? '#1a73e8' : '#333',
                              cursor: 'pointer',
                              fontWeight: showChinese ? '600' : '400',
                              fontSize: '0.9rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {showChinese ? '隐藏中文' : '显示中文'}
                          </button>
                        </div>
                      )}
                      {/* 单词标签 */}
                      {currentWordTab === 'words' && filteredWordsByStatus.length === 0 ? (
                        <p style={{
                          color: 'rgba(26, 43, 76, 0.6)',
                          fontSize: '1rem',
                          textAlign: 'center',
                          padding: '2rem 0'
                        }}>
                          {selectedVideo ? (wordFilter === 'all' ? '该视频还没有单词数据' : `没有${wordFilter === 'unmarked' ? '未标记' : wordFilter === 'known' ? '认识' : '不认识'}的单词`) : '请从左侧选择一个视频'}
                        </p>
                      ) : currentWordTab === 'words' && selectedVideo && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '1rem'
                        }}>
                          {filteredWordsByStatus.map((word) => (
                      <div key={word.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: word.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : word.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 右上角垂直按钮组 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                          {word.cambridgeLevel && (
                            <div style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #1a73e8',
                              backgroundColor: 'rgba(26, 115, 232, 0.1)',
                              color: '#1a73e8',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'default'
                            }}>
                              {word.cambridgeLevel}
                            </div>
                          )}
                          <button
                            onClick={() => updateWordStatus(word.id, 'known')}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #28a745',
                              backgroundColor: word.status === 'known' ? '#28a745' : 'white',
                              color: word.status === 'known' ? 'white' : '#28a745',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="认识"
                          >
                            ✓ 认识
                          </button>
                          <button
                            onClick={() => saveUnknownWord(word)}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #dc3545',
                              backgroundColor: word.status === 'unknown' ? '#dc3545' : 'white',
                              color: word.status === 'unknown' ? 'white' : '#dc3545',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="不认识，添加到单词卡"
                          >
                            ✗ 不认识
                          </button>
                        </div>
                        
                        {/* 单词和查词按钮 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 单词 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {word.word}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              minHeight: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {showAnnotations && word.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {word.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(word.word)}&type=2`);
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      console.log('有道TTS响应超时，使用备用发音');
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(word.word);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    }, 3000); // 3秒超时
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      clearTimeout(timeout);
                                    });
                                    
                                    audio.play().then(() => {
                                      clearTimeout(timeout);
                                    }).catch(error => {
                                      clearTimeout(timeout);
                                      console.log('有道TTS失败，使用浏览器发音:', error);
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(word.word);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    // 发生错误时使用备用方案
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(word.word);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    } catch (e2) {
                                      console.error('备用发音也失败:', e2);
                                    }
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {word.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(word.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {word.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {word.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* 英文定义 */}
                        <div style={{
                          minHeight: '40px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && word.definition && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                英译
                              </div>
                              <div style={{
                                fontSize: '0.9rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {word.definition}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 联想词 */}
                        <div style={{
                          minHeight: '30px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && word.synonyms && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                联想词
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {word.synonyms}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            position: 'relative'
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {word.sentence || '无例句'}
                          </div>
                          {showChinese && (word.sentenceChinese || word.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {word.sentenceChinese || '无中文翻译'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                      {/* 短语标签 */}
                      {currentWordTab === 'phrases' && filteredPhrasesByStatus.length === 0 ? (
                        <p style={{
                          color: 'rgba(26, 43, 76, 0.6)',
                          fontSize: '1rem',
                          textAlign: 'center',
                          padding: '2rem 0'
                        }}>
                          {selectedVideo ? (phraseFilter === 'all' ? '该视频还没有短语数据' : `没有${phraseFilter === 'unmarked' ? '未标记' : phraseFilter === 'known' ? '认识' : '不认识'}的短语`) : '请从左侧选择一个视频'}
                        </p>
                      ) : currentWordTab === 'phrases' && selectedVideo && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '1rem'
                        }}>
                          {filteredPhrasesByStatus.map((phrase) => (
                      <div key={phrase.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: phrase.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : phrase.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 右上角垂直按钮组 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                          {phrase.cambridgeLevel && (
                            <div style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #1a73e8',
                              backgroundColor: 'rgba(26, 115, 232, 0.1)',
                              color: '#1a73e8',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'default'
                            }}>
                              {phrase.cambridgeLevel}
                            </div>
                          )}
                          <button
                            onClick={() => updatePhraseStatus(phrase.id, 'known')}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #28a745',
                              backgroundColor: phrase.status === 'known' ? '#28a745' : 'white',
                              color: phrase.status === 'known' ? 'white' : '#28a745',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="认识"
                          >
                            ✓ 认识
                          </button>
                          <button
                            onClick={() => updatePhraseStatus(phrase.id, 'unknown')}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #dc3545',
                              backgroundColor: phrase.status === 'unknown' ? '#dc3545' : 'white',
                              color: phrase.status === 'unknown' ? 'white' : '#dc3545',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="不认识"
                          >
                            ✗ 不认识
                          </button>
                        </div>
                        
                        {/* 短语 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 短语 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {phrase.phrase}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              minHeight: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {showAnnotations && phrase.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {phrase.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(phrase.phrase)}&type=2`);
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      console.log('有道TTS响应超时，使用备用发音');
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(phrase.phrase);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    }, 3000); // 3秒超时
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      clearTimeout(timeout);
                                    });
                                    
                                    audio.play().then(() => {
                                      clearTimeout(timeout);
                                    }).catch(error => {
                                      clearTimeout(timeout);
                                      console.log('有道TTS失败，使用浏览器发音:', error);
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(phrase.phrase);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    // 发生错误时使用备用方案
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(phrase.phrase);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    } catch (e2) {
                                      console.error('备用发音也失败:', e2);
                                    }
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {phrase.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(phrase.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {phrase.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {phrase.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* 英文定义 */}
                        <div style={{
                          minHeight: '40px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && phrase.definition && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                英译
                              </div>
                              <div style={{
                                fontSize: '0.9rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {phrase.definition}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 联想词 */}
                        <div style={{
                          minHeight: '30px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && phrase.synonyms && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                联想词
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {phrase.synonyms}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 白色底框 - 例句 */}
                        <div style={{
                          minHeight: '40px',
                          marginBottom: '0.7rem'
                        }}>
                          {showAnnotations && phrase.definition !== undefined && phrase.definition !== '' && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                英译
                              </div>
                              <div style={{
                                fontSize: '0.9rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {phrase.definition}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            position: 'relative'
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {phrase.sentence || '无例句'}
                          </div>
                          {showChinese && (phrase.sentenceChinese || phrase.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {phrase.sentenceChinese || '无中文翻译'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                      {/* 自然科普词汇标签 */}
                      {currentWordTab === 'expressions' && filteredExpressionsByStatus.length === 0 ? (
                        <p style={{
                          color: 'rgba(26, 43, 76, 0.6)',
                          fontSize: '1rem',
                          textAlign: 'center',
                          padding: '2rem 0'
                        }}>
                          {selectedVideo ? (expressionFilter === 'all' ? '该视频还没有自然科普词汇数据' : `没有${expressionFilter === 'unmarked' ? '未标记' : expressionFilter === 'known' ? '认识' : '不认识'}的自然科普词汇`) : '请从左侧选择一个视频'}
                        </p>
                      ) : currentWordTab === 'expressions' && selectedVideo && (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '1rem'
                        }}>
                          {filteredExpressionsByStatus.map((expression) => (
                      <div key={expression.id} style={{
                        marginBottom: '1.5rem',
                        padding: '1.25rem',
                        borderRadius: '24px',
                        backgroundColor: expression.status === 'known' ? 'rgba(40, 167, 69, 0.1)' : expression.status === 'unknown' ? 'rgba(220, 53, 69, 0.35)' : 'rgba(240, 240, 240, 0.3)',
                        border: '2px solid rgba(0, 102, 255, 0.15)',
                        boxShadow: '0 6px 16px rgba(0, 102, 255, 0.08)',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0, 102, 255, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.08)';
                      }}>
                        {/* 右上角垂直按钮组 */}
                        <div style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.3rem',
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          zIndex: 1
                        }}>
                          {expression.cambridgeLevel && (
                            <div style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #1a73e8',
                              backgroundColor: 'rgba(26, 115, 232, 0.1)',
                              color: '#1a73e8',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'default'
                            }}>
                              {expression.cambridgeLevel}
                            </div>
                          )}
                          <button
                            onClick={() => updateExpressionStatus(expression.id, 'known')}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #28a745',
                              backgroundColor: expression.status === 'known' ? '#28a745' : 'white',
                              color: expression.status === 'known' ? 'white' : '#28a745',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="认识"
                          >
                            ✓ 认识
                          </button>
                          <button
                            onClick={() => updateExpressionStatus(expression.id, 'unknown')}
                            style={{
                              padding: '0.3rem 0.6rem',
                              borderRadius: '4px',
                              border: '1px solid #dc3545',
                              backgroundColor: expression.status === 'unknown' ? '#dc3545' : 'white',
                              color: expression.status === 'unknown' ? 'white' : '#dc3545',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              width: '70px',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="不认识"
                          >
                            ✗ 不认识
                          </button>
                        </div>
                        
                        {/* 自然科普词汇 */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          marginBottom: '0.7rem',
                          flexWrap: 'nowrap',
                          width: '100%',
                          paddingRight: '90px'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0
                          }}>
                            {/* 自然科普词汇 */}
                            <div
                              style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#1a73e8',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                marginBottom: '0.3rem'
                              }}
                            >
                              {expression.expression || expression.word}
                            </div>
                            
                            {/* 音标、发音 */}
                            <div style={{
                              marginBottom: '0.7rem',
                              minHeight: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'nowrap'
                            }}>
                              {showAnnotations && expression.phonetic && (
                                <div style={{
                                  fontSize: '1rem',
                                  color: 'rgba(0, 0, 0, 0.7)',
                                  fontFamily: 'Arial, sans-serif',
                                  whiteSpace: 'nowrap',
                                  marginRight: '1rem'
                                }}>
                                  {expression.phonetic}
                                </div>
                              )}
                              <button
                                onClick={() => {
                                  try {
                                    // 先尝试使用有道语音合成 API
                                    const wordToSpeak = expression.expression || expression.word;
                                    const audio = new Audio(`https://dict.youdao.com/dictvoice?audio=${encodeURIComponent(wordToSpeak)}&type=2`);
                                    
                                    // 设置较长的超时时间
                                    const timeout = setTimeout(() => {
                                      console.log('有道TTS响应超时，使用备用发音');
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    }, 3000); // 3秒超时
                                    
                                    audio.addEventListener('canplaythrough', () => {
                                      clearTimeout(timeout);
                                    });
                                    
                                    audio.play().then(() => {
                                      clearTimeout(timeout);
                                    }).catch(error => {
                                      clearTimeout(timeout);
                                      console.log('有道TTS失败，使用浏览器发音:', error);
                                      // 备用方案：使用浏览器发音
                                      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      // 尝试使用高质量语音
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    });
                                  } catch (e) {
                                    console.error('发音失败:', e);
                                    // 发生错误时使用备用方案
                                    try {
                                      const utterance = new SpeechSynthesisUtterance(expression.word);
                                      utterance.lang = 'en-US';
                                      utterance.rate = 0.85;
                                      const voices = speechSynthesis.getVoices();
                                      const highQualityVoice = voices.find(v => v.lang === 'en-US' && (v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')));
                                      if (highQualityVoice) {
                                        utterance.voice = highQualityVoice;
                                      }
                                      speechSynthesis.speak(utterance);
                                    } catch (e2) {
                                      console.error('备用发音也失败:', e2);
                                    }
                                  }
                                }}
                                style={{
                                  width: '30px',
                                  height: '30px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(200, 200, 200, 0.2)',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                title="听发音"
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                                  e.target.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'rgba(200, 200, 200, 0.2)';
                                  e.target.style.transform = 'scale(1)';
                                }}
                              >
                                🔊
                              </button>
                            </div>
                            
                            {/* 词性 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {expression.partOfSpeech && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    词性
                                  </div>
                                  <div style={{
                                    fontSize: '1.1rem',
                                    color: 'rgba(0, 0, 0, 0.9)',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {abbreviatePartOfSpeech(expression.partOfSpeech)}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* 中文翻译 */}
                            <div style={{
                              marginBottom: '0.6rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {expression.chineseTranslation && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <div style={{
                                    background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    marginRight: '0.5rem',
                                    whiteSpace: 'nowrap',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                  }} onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                  }} onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}>
                                    中译
                                  </div>
                                  {showChinese && (
                                    <div style={{
                                      fontSize: '1rem',
                                      color: 'rgba(0, 0, 0, 0.9)',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {expression.chineseTranslation}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                          </div>
                        </div>
                        
                        {/* 英文定义 */}
                        <div style={{
                          minHeight: '40px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && expression.definition && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                英译
                              </div>
                              <div style={{
                                fontSize: '0.9rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {expression.definition}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 联想词 */}
                        <div style={{
                          minHeight: '30px',
                          marginBottom: '0.6rem'
                        }}>
                          {showAnnotations && expression.synonyms && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              marginTop: '0.2rem'
                            }}>
                              <div style={{
                                background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                                color: 'white',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                marginRight: '0.5rem',
                                whiteSpace: 'nowrap',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }} onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1557b0, #003a80)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                              }} onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(45deg, #1a73e8, #0056b3)';
                                e.currentTarget.style.boxShadow = 'none';
                              }}>
                                联想词
                              </div>
                              <div style={{
                                fontSize: '0.85rem',
                                color: 'rgba(0, 0, 0, 0.6)',
                                flex: 1
                              }}>
                                {expression.synonyms}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* 白色底框 - 例句 */}
                        <div 
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            padding: '0.8rem',
                            marginTop: '0.7rem',
                            position: 'relative'
                          }}
                        >
                          <div style={{
                            background: 'linear-gradient(45deg, #1a73e8, #0056b3)',
                            color: 'white',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            display: 'inline-block'
                          }}>
                            原文例句
                          </div>
                          <div style={{
                            fontSize: '0.95rem',
                            color: '#333',
                            marginBottom: '0.3rem',
                            lineHeight: '1.5'
                          }}>
                            {expression.sentence || '无例句'}
                          </div>
                          {showChinese && (expression.sentenceChinese || expression.sentenceChinese === '') && (
                            <div style={{
                              fontSize: '0.85rem',
                              color: 'rgba(0, 0, 0, 0.6)',
                              lineHeight: '1.4'
                            }}>
                              {expression.sentenceChinese || '无中文翻译'}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>


    </div>
  );
};

export default EnglishCards;